/**
 * KAN-877: варианты кнопки "Позвонить" с правом на звонок (рендер, логика в
 * хуках useManagerCall / useCallTargets).
 *
 * PlainCall        - адресата выбирает сервер (прежний вид KAN-833).
 * EagerTargetsCall - карточка одного клиента: адресаты грузятся сразу.
 *                    Пока грузятся - кнопка ждёт без номера; ошибка - кнопка
 *                    выключена, пояснение и "Повторить"; [] - PlainCall.
 * PickTargetCall   - строка списка: адресаты по клику, при нужде выбор.
 */
import { useRef } from 'react';
import { useCallTargetPicker, useCallTargets } from '@/hooks/useCallTargets';
import { useManagerCall } from '@/hooks/useManagerCall';
import {
  NO_CABINET_CALL_NUMBER_HINT,
  NO_CABINET_CALL_NUMBER_SHORT,
  useCabinetHasCallNumber,
} from '@/hooks/useCabinetCallNumber';
import { CALL_TARGETS_ERROR, callTargetButtonLabel, isCardPersonTarget } from '@/lib/callTargets';
import type { CallTarget } from '@/services/api/callTargets';
import { CallRow, CallTargetPicker, type CallButtonSize } from './ManagerCallParts';

export interface CallViewProps {
  clientId: string;
  phone: string;
  testId: string;
  className: string;
  size: CallButtonSize;
  showPhone: boolean;
}

export const CALL_LABEL = 'Позвонить';
const CALL_HINT = 'Платформа позвонит вам, после ответа соединит с клиентом';

function NoNumberCaption({ testId, show }: { testId: string; show: boolean }) {
  if (!show) return null;
  return (
    <span data-testid={`${testId}-no-number`} className="w-full text-xs text-gray-500">
      {NO_CABINET_CALL_NUMBER_SHORT}
    </span>
  );
}

function useCallBase(clientId: string) {
  const call = useManagerCall(clientId);
  const hasNumber = useCabinetHasCallNumber();
  return { call, hasNumber, title: hasNumber ? CALL_HINT : NO_CABINET_CALL_NUMBER_HINT };
}

export function PlainCall({ clientId, phone, testId, className, size, showPhone }: CallViewProps) {
  const { call, hasNumber, title } = useCallBase(clientId);
  return (
    <CallRow
      testId={testId}
      label={CALL_LABEL}
      phone={showPhone ? phone : undefined}
      title={title}
      disabled={!hasNumber}
      pending={call.isPending}
      size={size}
      onClick={() => {
        if (hasNumber) call.mutate();
      }}
      className={className}
    >
      <NoNumberCaption testId={testId} show={!hasNumber && size === 'lg'} />
    </CallRow>
  );
}

/** Номер рядом с кнопкой адресата: номер человека карточки при showPhone={false} уже виден рядом. */
function targetPhone(target: CallTarget, isCardPerson: boolean, view: CallViewProps): string | undefined {
  if (isCardPerson && !view.showPhone) return undefined;
  return target.phone || (isCardPerson ? view.phone : undefined);
}

export function EagerTargetsCall(view: CallViewProps) {
  const { clientId, testId, className, size } = view;
  const { call, hasNumber, title } = useCallBase(clientId);
  const query = useCallTargets(clientId);
  const targets = query.data;

  if (!targets) {
    const failed = query.isError;
    return (
      <CallRow
        testId={testId}
        label={CALL_LABEL}
        title={failed ? CALL_TARGETS_ERROR : title}
        disabled
        pending={!failed || query.isFetching}
        size={size}
        onClick={() => undefined}
        className={className}
      >
        {failed && (
          <span data-testid={`${testId}-targets-error`} className="inline-flex items-center gap-2 text-xs text-red-600">
            {CALL_TARGETS_ERROR}
            <button
              type="button"
              data-testid={`${testId}-retry`}
              onClick={() => void query.refetch()}
              disabled={query.isFetching}
              className="text-blue-600 underline hover:no-underline disabled:opacity-50"
            >
              Повторить
            </button>
          </span>
        )}
      </CallRow>
    );
  }
  if (targets.length === 0) return <PlainCall {...view} />;

  const rows = targets.map((target, index) => {
    // Первый адресат - человек карточки (client или referral), если сервер не сказал иначе.
    const isCardPerson = index === 0 && isCardPersonTarget(target);
    const single = targets.length === 1;
    return (
      <CallRow
        key={`${target.target}-${index}`}
        testId={single ? testId : `${testId}-${target.target}`}
        label={single && isCardPerson ? CALL_LABEL : callTargetButtonLabel(target)}
        phone={targetPhone(target, isCardPerson, view)}
        title={title}
        disabled={!hasNumber}
        pending={call.isPending}
        size={size}
        onClick={() => {
          if (hasNumber) call.mutate(target.target);
        }}
      />
    );
  });
  return (
    <span data-testid={`${testId}-targets`} className={`flex flex-col items-start gap-1.5 ${className}`}>
      {rows}
      <NoNumberCaption testId={testId} show={!hasNumber && size === 'lg'} />
    </span>
  );
}

export function PickTargetCall({ clientId, phone, testId, className, size, showPhone }: CallViewProps) {
  const { call, hasNumber, title } = useCallBase(clientId);
  const picker = useCallTargetPicker(clientId, (target) => call.mutate(target));
  const buttonRef = useRef<HTMLButtonElement>(null);
  return (
    <span className={`relative inline-flex flex-wrap items-center ${className}`}>
      <CallRow
        testId={testId}
        buttonRef={buttonRef}
        label={CALL_LABEL}
        phone={showPhone ? phone : undefined}
        title={title}
        disabled={!hasNumber}
        pending={call.isPending || picker.loading}
        size={size}
        onClick={() => {
          if (hasNumber) void picker.start();
        }}
      >
        <NoNumberCaption testId={testId} show={!hasNumber && size === 'lg'} />
      </CallRow>
      {picker.choices && (
        <CallTargetPicker
          testId={testId}
          targets={picker.choices}
          onChoose={picker.choose}
          onClose={picker.close}
          returnFocusRef={buttonRef}
        />
      )}
    </span>
  );
}
